import { ReactElement, useEffect, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	renderReactDialog,
	ValidationError,
	SecretValueInput,
	FormattedText,
	Toolbar,
} from "client/Components";
import { convertServerErrors, createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { IFormHandler, useFormHandler } from "client/Forms";
import { HttpRequestError } from "client/Api/httpClient";
import { ClientValidationErrorModel } from "client/Api/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	isUrl
} from "client/Validation/rules";
import {
	get,
	update,
	create,
	TopDeskConnectionModel,
	TopDeskConnectionSaveModel
} from "client/Api/TopDeskConfiguration/topDeskConfiguration";
import { InputValidationResultKind } from "client/Validation/input";
import { AlertManager, useAlerts} from "client/Hooks";

const originalState: TopDeskConnectionSaveModel = {
	name: "",
	serverUrl: "",
	username: "",
	password: {
		value: "",
		containsNewValue: true
	},
};

const validationBuilder: IValidationConfiguration<TopDeskConnectionSaveModel> = createValidationConfiguration<TopDeskConnectionSaveModel>();

validationBuilder.forScalar("name").add(
	isNotNullNorUndefined(),
	isNotEmpty()
);

validationBuilder.forScalar("username").add(
	isNotNullNorUndefined(),
	isNotEmpty()
);

validationBuilder.forObject("password").add(
	isNotNullNorUndefined()
);

validationBuilder.forScalar("serverUrl").add(
	isNotNullNorUndefined(),
	isNotEmpty(),
	isUrl({
		allowHttpsOnly: true
	})
);

export type EditTopDeskConnectionDialogProps = {
	onClose(): void;
	onSaved(connectionId: number): void;
	id?: number;
};

export const EditTopDeskConnectionDialog = (props: EditTopDeskConnectionDialogProps): ReactElement => {
	const {
		id,
		onClose,
		onSaved
	} = props;

	const [isSaving, setSaving] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);

	const [model, setModel] = useState<TopDeskConnectionSaveModel>(originalState);
	const formHandler: IFormHandler<TopDeskConnectionSaveModel> = useFormHandler(model, setModel, validationBuilder);
	const alertManager: AlertManager = useAlerts();
	const isNewConnection: boolean = !id;

	const titleLocalizationKey: LocalizationKey = isNewConnection
		? "_NewNameConnection"
		: "_EditNameConnection";

	const saveLocalizationKey: LocalizationKey = isNewConnection
		? "_Add"
		: "_Save";

	useEffect(
		() => {
			async function load(id: number, signal: AbortSignal): Promise<void> {
				setLoading(true);
				try {
					const data: TopDeskConnectionModel = await get(id, signal);
					setModel(convertExistingItem(data));
					setLoading(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			if (id) {
				const cancellation = new AbortController();
				load(id, cancellation.signal);
				return () => cancellation.abort();
			} else {
				setModel(originalState);
				return undefined;
			}
		},
		[alertManager, id, onClose]
	);

	const saveHandler =	async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		if ((await formHandler.validation.validate()).kind === InputValidationResultKind.Invalid) {
			return;
		}

		setSaving(true);
		try {
			let savedId: number;
			if (!id) {
				savedId = (await create(model)).id;
			} else {
				await update(id, model);
				savedId = id;
			};

			onSaved(savedId);
			onClose();
		} catch(e) {
			let wasHandled: boolean = false;
			if (e instanceof HttpRequestError && e.statusCode === 400) {
				try {
					const errors = e.readJson<ClientValidationErrorModel[]>();
					formHandler.validation.setServerErrors(convertServerErrors(errors));

					wasHandled = true;
				} catch (exception) {
					console.error(exception);
				}
			}

			if (!wasHandled) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}

			setSaving(false);
		}
	};

	const serverUrlChangeHandler = (): void => {
		const passwordReducer = formHandler.reducer.for("password");
		if (!passwordReducer.value.containsNewValue) {
			passwordReducer.setValue({
				containsNewValue: true,
				value: ""
			});
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isSaving || isLoading}
			trackingId="top-desk-connection-dialog"
		>
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleLocalizationKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_TopDesk")}
						]}
					/>
				}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput {...formHandler.for("name").attachToTextInput()}	/>
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_TopDeskUrl")}>
						<TextInput {...formHandler
							.for("serverUrl")
							.attachToTextInput()
							.withOnChange(serverUrlChangeHandler)}
						/>
						<ValidationError {...formHandler.for("serverUrl").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_Username")}>
						<TextInput {...formHandler.for("username").attachToTextInput()} />
						<ValidationError {...formHandler.for("username").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_AppPassword")}>
						<SecretValueInput
							{...formHandler.for("password").attachToSecretValueInput()}
							containsInitialValue
						/>
						<ValidationError {...formHandler
							.for("password")
							.for("value")
							.attachToValidationError()}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveLocalizationKey)}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

EditTopDeskConnectionDialog.displayName = "EditTopDeskConnectionDialog";

function convertExistingItem(data: TopDeskConnectionModel): TopDeskConnectionSaveModel {
	return {
		name: data.name,
		username: data.username,
		serverUrl: data.serverUrl,
		password: {
			containsNewValue: false,
			value: null
		}
	};
}

function openEditDialog(
	onSaved: (connectionId: number) => void,
	id?: number
): void {
	renderReactDialog(
		(closeHandler) => (
			<EditTopDeskConnectionDialog
				id={id}
				onClose={closeHandler}
				onSaved={onSaved}
			/>
		)
	);
};

export {
	openEditDialog
};
